import { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import Router from 'next/router';
import { closeModal, showModal }  from 'src/modules/shared/modal/actions';
import { updateMarketResearchPreference } from 'src/modules/user/shared/actions';
import { fetchCurrentUser } from 'src/modules/shared/context/actions';
import i18n from 'src/utils/translate';
import { trackFootfallEvent } from 'src/utils/footfall';
import { Event } from 'src/constants/footfall';
import { State } from 'src/constants/types';
import { ModalType } from 'src/constants/modalTypes';
import Modal from 'src/components/modals/modal_v2';
import Survey, { Screen } from 'src/components/survey/survey';
import FindCommunityScreen from './signupSurveyScreens/findCommunityScreen';
import ResearchOptInScreen from './signupSurveyScreens/researchOptInScreen';
import UsernameScreen from './signupSurveyScreens/usernameScreen';
import AcceptTermsScreen from './signupSurveyScreens/acceptTermsScreen';

export enum SignupFlow {
  Community = 'Community',
  Invitation = 'Invitation',
}

export default function SignupSurvey(): JSX.Element {
  const dispatch = useDispatch();
  const { signupFlow, signupUser, redirectPage } = useSelector(({ modal }: State) => modal.modalProps);
  const isSignupFromPost = useSelector(({ pages }: State) => pages.singlePost.isSecondPageBlockMsgBoxVisible);

  const [currentPosition, setCurrentPosition] = useState(0);
  const [username, setUsername] = useState('');
  const [userJoinedCommunities, setUserJoinedCommunities] = useState(false);
  const [showJoinCommunityValidation, setShowJoinCommunityValidation] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const isSkipFindCommunities = signupFlow === SignupFlow.Community || signupFlow === SignupFlow.Invitation;

  const onFinish = async (acceptResearch = true) => {
    trackFootfallEvent(Event.CLICKED, { clickType: `opt-in-research-${acceptResearch ? 'yes' : 'no'}` });
    await dispatch(fetchCurrentUser());
    dispatch(updateMarketResearchPreference(acceptResearch));
    setIsSubmitting(true);
    await dispatch(closeModal());

    if (redirectPage) return redirectPage();
    if (signupFlow === SignupFlow.Invitation) return;
    if (isSignupFromPost || signupFlow !== SignupFlow.Community && Router.route !== '/') {
      dispatch(showModal({ modalType: ModalType.TakeProfileSurveyOnSignup }));
    } else {
      dispatch(showModal({ modalType: ModalType.TakeProfileSurveyOnSignup, modalProps: { redirectOnClose: '/explore' } }));
    }
  };

  const screens: Screen[] = [
    {
      id: 1,
      key: 'username',
      title: i18n.t('Create a username'),
      content: <UsernameScreen setUsername={setUsername} />,
    },
    {
      id: 2,
      key: 'acceptTerms',
      title: i18n.t('Our Terms'),
      content: <AcceptTermsScreen />,
      buttonText: i18n.t('Next'),
    },
    {
      id: 3,
      key: 'findCommunities',
      title: i18n.t('Find your communities'),
      content: (
        <FindCommunityScreen
          setShowJoinCommunityValidation={setShowJoinCommunityValidation}
          setUserJoinedCommunities={setUserJoinedCommunities}
          showJoinCommunityValidation={showJoinCommunityValidation}
        />
      ),
      buttonText: i18n.t('Next'),
    },
    {
      id: 4,
      key: 'acceptResearch',
      title: i18n.t('One last thing...'),
      content: <ResearchOptInScreen onFinish={onFinish} />,
      buttonText: i18n.t('Yes'),
    },
  ];

  const filteredScreens = isSkipFindCommunities ? screens.filter(({ key }) => key !== 'findCommunities') : screens;

  const currentScreen = filteredScreens[currentPosition];

  const progressValues = isSkipFindCommunities
    ? [50, currentPosition >= 1 ? 25 : 0, currentPosition >= 2 ? 15 : 0]
    : [40, currentPosition >= 1 ? 20 : 0, currentPosition >= 2 ? 20 : 0, currentPosition >= 3 ? 10 : 0];

  const surveyProgress = progressValues.reduce((a, b) => a + b, 0);

  const isValidScreen = () => {
    if (isSubmitting) {
      return false;
    }
    switch (currentScreen.key) {
      case 'username':
        return !!username;
      default:
        return true;
    }
  };

  const onPositionChanged = async (position: number) => {
    switch (currentScreen.key) {
      case 'username':
        trackFootfallEvent(Event.CLICKED, { clickType: 'join-username-entered' });
        break;
      case 'acceptTerms':
        trackFootfallEvent(Event.CLICKED, { clickType: 'join-agreed' });
        // creates user after accepting terms
        await signupUser({ username });
        break;
      case 'findCommunities':
        trackFootfallEvent(Event.CLICKED, { clickType: 'join-find-communities' });
    }
    setCurrentPosition(position);
  };

  const renderScreen = (screen: Screen) => (
    <div className="survey__screen survey__screen--no-header signup-box__questions" key={screen.key}>
      <h3 className="signup-box__questions__title">{screen.title}</h3>
      {screen.content}
    </div>
  );

  const showFindCommunityError = () => {
    setShowJoinCommunityValidation(!userJoinedCommunities);
  };

  return (
    <Modal customHeader={true} noClose={true}>
      <Survey
        className="signup-box__survey"
        hideHeader={true}
        isNextButtonDisabled={!isValidScreen()}
        progress={surveyProgress}
        renderScreen={renderScreen}
        screens={filteredScreens}
        onFinish={onFinish}
        onNext={currentScreen.key !== 'findCommunities' ? null : (userJoinedCommunities ? null : showFindCommunityError)}
        onPositionChanged={onPositionChanged}
      />
    </Modal>
  );
}

